import {
  Badge,
  Box,
  Button,
  DiscoveryIndicator,
  Icon,
  Menu,
  MenuGroup,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuShortcut,
  MenuTrigger,
  Text,
  toastManager,
  useIsMobile,
} from "@salestrak/ui";
import { WorkspaceSelector } from "#app/components/navigation/workspace-selector";
import { GlobalSearchBar } from "#app/components/global-search/global-search-bar";
import { useGlobalSearchActions } from "#app/components/global-search/global-search-context";
import { useAuth } from "#app/lib/auth-context";
import { useAnalytics, AnalyticsContext, AuthEvent } from "#app/lib/analytics";
import { useAppData } from "#app/lib/data";
import { EntityTag } from "#app/components/entity";
import { useSidebarNav } from "./use-sidebar-nav";
import { HelpSubMenu } from "#app/components/navigation/help-sub-menu";
import { ThemeSubMenu } from "#app/components/navigation/theme-sub-menu";
import { useThemeAppearance } from "#app/components/theme/theme-appearance-provider";
import { WhatsNewDialog } from "#app/components/navigation/whats-new-dialog";
import { AboutDialog } from "#app/components/navigation/about-dialog";
import { FeedbackDialog } from "#app/components/navigation/feedback-dialog";
import React from "react";
import { useRouter } from '#app/lib/hooks/router-compat';
import { APP_VERSION as APP_VERSION_RAW } from "#app/lib/app-version";
import { useAnnouncements } from "#app/hooks/use-announcements";
import { copyToClipboard } from "#app/lib/utils/copy-to-clipboard";
import { readJWT } from "#app/lib/jwt";

const APP_VERSION = `v${APP_VERSION_RAW}`;

export function SiteHeader() {
  const isMobile = useIsMobile();
  const { currentUser } = useAppData();
  const { track } = useAnalytics();
  const { signOut, state: authState } = useAuth();
  const { navigate } = useSidebarNav();
  const { openSearch } = useGlobalSearchActions();
  const router = useRouter();

  const isSuperuser = authState.accessToken
    ? ((readJWT(authState.accessToken).superuser as boolean) ?? false)
    : false;

  const { entries, unseenKeys, unseenCount, markAllSeen } = useAnnouncements();

  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = React.useState(false);
  const [aboutOpen, setAboutOpen] = React.useState(false);
  // Mark everything seen when the dialog closes, not when it opens: on open the
  // discovery query may not have resolved yet (markAllSeen no-ops on empty
  // data), and marking on close still captures every entry the user just read
  // while showing live unseen dots the whole time it's open.
  const closeWhatsNew = () => {
    setWhatsNewOpen(false);
    markAllSeen();
  };

  const userMenu = (
    <Menu>
      <MenuTrigger
        render={
          <Button
            aria-label="Account menu"
            className="rounded-full"
            size="icon"
            variant="ghost"
          />
        }
      >
        <EntityTag
          display="visual"
          entity={{
            kind: "user",
            id: currentUser?.userId ?? "me",
            user: currentUser ?? null,
          }}
          interactive={false}
          size="md"
        />
      </MenuTrigger>
      <MenuPopup
        align="end"
        className="max-w-[320px] min-w-[230px]"
        side="bottom"
      >
        <MenuGroup>
          <Box paddingX="xs" paddingY="xs">
            <EntityTag
              entity={{
                kind: "user",
                id: currentUser?.userId ?? "me",
                user: currentUser ?? null,
              }}
              interactive={false}
              subtitle
              weight="semibold"
            />
          </Box>
        </MenuGroup>
        <MenuSeparator />
        <MenuGroup>
          <MenuItem onClick={() => navigate("/settings/profile")}>
            <Icon color="muted" name="user" size="md" />
            <Text>Profile</Text>
          </MenuItem>
          <ThemeSubMenu />
          <MenuItem onClick={() => navigate("/settings/preferences")}>
            <Icon color="muted" name="sliders-horizontal" size="md" />
            <Text>Preferences</Text>
          </MenuItem>
        </MenuGroup>
        <MenuSeparator />
        <MenuGroup>
          <HelpSubMenu onSendFeedback={() => setFeedbackOpen(true)} />
          <MenuItem onClick={() => setWhatsNewOpen(true)}>
            <Icon color="muted" name="gift" size="md" />
            <Text>What's new</Text>
            {/* MenuShortcut is a bare <kbd> with no render slot, so the count
              * chip takes its `ms-auto` placement directly. */}
            {unseenCount > 0 && (
              <Badge className="ms-auto" size="sm">
                {unseenCount}
              </Badge>
            )}
          </MenuItem>
          <MenuItem onClick={() => setAboutOpen(true)}>
            <Icon color="muted" name="info" size="md" />
            <Text>About SalesTrak</Text>
            <MenuShortcut>{APP_VERSION}</MenuShortcut>
          </MenuItem>
        </MenuGroup>
        {isSuperuser && (
          <>
            <MenuSeparator />
            <MenuGroup>
              <MenuItem onClick={() => router.push("/(app)/admin")}>
                <Icon color="muted" name="shield" size="md" />
                <Text>Admin</Text>
              </MenuItem>
            </MenuGroup>
          </>
        )}
        <MenuSeparator />
        <MenuGroup>
          <MenuItem
            onClick={() => {
              track({ context: AnalyticsContext.Auth, event: AuthEvent.SignOut });
              signOut();
            }}
          >
            <Icon color="muted" name="log-out" size="md" />
            <Text>Sign out</Text>
          </MenuItem>
        </MenuGroup>
      </MenuPopup>
    </Menu>
  );

  return (
    <>
      {/*
        * On desktop the header spans the full width at the very top of the
        * shell — workspace switcher at the left, search centered, account menu
        * at the right. On mobile the shell renders it inside the nav drawer
        * instead, so these controls present with the navigation; the drawer
        * trigger therefore lives in each page-level header (PageHeader,
        * SettingsMobileHeader, the map's overlay chip), not here.
        */}
      <Box
        align="center"
        as="header"
        // Fixed height belongs to the pinned desktop bar; in the mobile
        // drawer the row sizes to its controls.
        className={isMobile ? undefined : "h-13"}
        direction="row"
        gap="sm"
        paddingLeft="sm"
        paddingRight="md"
        paddingY={isMobile ? "sm" : undefined}
        position="relative"
        shrink={false}
      >
        {/* On mobile the selector claims all free width so the search and
            account buttons pin to the right edge. */}
        <Box direction="column" grow={isMobile} minWidth={isMobile ? 0 : undefined}>
          <WorkspaceSelector compact fill={isMobile} />
        </Box>
        {/* Centered on the HEADER, not on the leftover flex space — the
            workspace selector and the account cluster have unequal widths, so
            flex centering would sit visibly off-axis. The overlay ignores
            pointer events except on the bar itself. */}
        {!isMobile && (
          <Box
            align="center"
            fill="absolute"
            justify="center"
            pointerEvents="none"
          >
            <Box pointerEvents="auto">
              <GlobalSearchBar />
            </Box>
          </Box>
        )}
        {/* Spacer keeps the right cluster pinned right now that the search no
            longer occupies the middle flex slot. */}
        {!isMobile && <Box grow />}
        <Box align="center" direction="row" gap="xs">
          {isMobile && (
            <Button
              aria-label="Search everything"
              className="rounded-full"
              onClick={openSearch}
              size="icon"
              variant="secondary"
            >
              <Icon name="search" size="sm" />
            </Button>
          )}
          <DiscoveryIndicator placement="top-right" visible={unseenCount > 0}>
            {userMenu}
          </DiscoveryIndicator>
        </Box>
      </Box>
      <FeedbackDialog onOpenChange={setFeedbackOpen} open={feedbackOpen} />
      <WhatsNewDialog
        entries={entries}
        onNavigate={(route) => {
          closeWhatsNew();
          navigate(route);
        }}
        onOpenChange={(open) => (open ? setWhatsNewOpen(true) : closeWhatsNew())}
        open={whatsNewOpen}
        unseenKeys={unseenKeys}
      />
      <AboutDialog onOpenChange={setAboutOpen} open={aboutOpen} />
    </>
  );
}
